// TelegramApprovers — the "Money-approval approvers" card on the Ops bot page.
//
// The reviewed Telegram accounts allowed to CONFIRM money (recharge) actions
// from the ops bot. Linking uses the Telegram LOGIN WIDGET: Telegram signs a
// user object with the bot token, the widget hands it back to a callback, and
// the FE posts it VERBATIM to POST /admin/telegram/link where the backend
// re-verifies the HMAC. The gate arms automatically once at least one approver
// is linked.
//
// SECURITY: the browser never holds the bot token. The widget signature is
// verified server-side; the FE only forwards the signed object and renders the
// safe approver fields the backend returns (no hash/token).
//
// GRACEFUL DEGRADATION: the real telegram-widget.js only renders its button
// when the bot's domain is registered in @BotFather AND the origin matches
// (so it shows nothing on localhost / an unregistered domain, and never in
// jsdom). The widget is therefore abstracted behind <TelegramLoginWidget/>,
// which injects the script and ALWAYS renders an inline note pointing at the
// runbook so the card degrades to a clear explanation rather than a blank box.

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  getTelegramConfig,
  linkApprover,
  listApprovers,
  unlinkApprover,
  type TelegramApprover,
  type TelegramWidgetUser,
} from "@/api/telegram";
import type { ApiError } from "@/api/client";
import { errMessage } from "@/lib/errMessage";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Modal, StatusPill } from "@/components/nexus";

// The global callback name the injected widget invokes via data-onauth. Kept
// stable so the script's `onauth` expression resolves; the component wires it to
// the current onAuth handler on every mount.
const WIDGET_CALLBACK = "onTelegramApproverAuth";

// TelegramLoginWidget — injects telegram-widget.js and registers the global
// callback the widget calls with the signed user object. In prod the script
// renders a "Log in with Telegram" button; where it can't (localhost / domain
// not registered / jsdom) nothing renders, so we always show the inline note.
export function TelegramLoginWidget({
  botUsername,
  onAuth,
}: {
  botUsername: string;
  onAuth: (user: TelegramWidgetUser) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Keep the latest handler in a ref so the injection effect can depend only on
  // botUsername (re-injecting the script on every render would flicker it).
  const onAuthRef = useRef(onAuth);
  onAuthRef.current = onAuth;

  useEffect(() => {
    if (!botUsername) return;
    const w = window as unknown as Record<string, unknown>;
    w[WIDGET_CALLBACK] = (user: TelegramWidgetUser) => onAuthRef.current(user);

    const script = document.createElement("script");
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.async = true;
    script.setAttribute("data-telegram-login", botUsername);
    script.setAttribute("data-size", "medium");
    script.setAttribute("data-request-access", "write");
    script.setAttribute("data-userpic", "false");
    script.setAttribute("data-onauth", `${WIDGET_CALLBACK}(user)`);

    const el = containerRef.current;
    el?.appendChild(script);
    return () => {
      delete w[WIDGET_CALLBACK];
      if (el) el.replaceChildren();
    };
  }, [botUsername]);

  return (
    <div className="grid gap-2">
      <div ref={containerRef} data-testid="telegram-login-widget" />
      <p className="text-xs text-nx-text-3">
        The “Log in with Telegram” button requires the bot’s domain to be
        registered in @BotFather. If no button appears (e.g. on localhost or an
        unregistered domain), see the runbook.
      </p>
    </div>
  );
}

function ApproverRow({
  approver,
  onUnlink,
}: {
  approver: TelegramApprover;
  onUnlink: (a: TelegramApprover) => void;
}) {
  const name = approver.username
    ? `@${approver.username}`
    : approver.first_name || String(approver.telegram_id);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-nx-border bg-nx-bg-2 px-3 py-2">
      <div className="grid gap-0.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-nx-text">
            {approver.first_name || name}
          </span>
          {approver.username && (
            <span className="font-mono text-xs text-nx-text-3">
              @{approver.username}
            </span>
          )}
          <StatusPill tone="credit">approver</StatusPill>
        </div>
        <span className="font-mono text-xs text-nx-text-3 tabular-nums">
          {approver.telegram_id}
        </span>
        <span className="text-xs text-nx-text-3">
          Linked {new Date(approver.verified_at * 1000).toLocaleString()} · by{" "}
          {approver.linked_by || "—"}
        </span>
      </div>
      <Button
        type="button"
        variant="destructive"
        size="sm"
        onClick={() => onUnlink(approver)}
      >
        Unlink
      </Button>
    </div>
  );
}

export function TelegramApprovers() {
  const [botUsername, setBotUsername] = useState<string | null>(null);
  const [approvers, setApprovers] = useState<TelegramApprover[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [linking, setLinking] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState<TelegramApprover | null>(
    null,
  );
  const [unlinking, setUnlinking] = useState(false);

  const loadApprovers = useCallback(async () => {
    const res = await listApprovers();
    setApprovers(res.approvers ?? []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    Promise.all([getTelegramConfig(), listApprovers()])
      .then(([cfg, list]) => {
        if (cancelled) return;
        setBotUsername(cfg.bot_username ?? "");
        setApprovers(list.approvers ?? []);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(errMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onAuth = useCallback(
    async (user: TelegramWidgetUser) => {
      setLinking(true);
      try {
        const approver = await linkApprover(user);
        toast.success(
          `Linked ${approver.username ? `@${approver.username}` : approver.first_name} as an approver`,
        );
        await loadApprovers();
      } catch (err) {
        // The backend answers a tampered/stale/wrong-token widget object with a
        // single sanitized reason (the hash never round-trips). Give it a clear
        // message; fall back to the generic sanitized message otherwise.
        const reason = ((err as ApiError)?.body as { reason?: string })?.reason;
        toast.error(
          reason === "telegram_verification_failed"
            ? "Telegram could not verify that login — try the button again."
            : errMessage(err),
        );
      } finally {
        setLinking(false);
      }
    },
    [loadApprovers],
  );

  async function onUnlink() {
    if (!confirmUnlink) return;
    setUnlinking(true);
    try {
      await unlinkApprover(confirmUnlink.id);
      setApprovers((prev) => prev.filter((a) => a.id !== confirmUnlink.id));
      toast.success("Approver unlinked");
      setConfirmUnlink(null);
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setUnlinking(false);
    }
  }

  if (loading) {
    return <Skeleton className="h-24 w-full" />;
  }

  return (
    <div className="grid gap-4">
      <p className="max-w-2xl text-sm text-nx-text-2">
        These Telegram accounts are the reviewed approvers allowed to confirm
        money (recharge) actions from the ops bot. The gate arms automatically
        once at least one is linked.
      </p>

      {loadError && (
        <p className="rounded-md bg-nx-danger-soft px-3 py-2 text-xs text-nx-danger">
          Could not load approvers: {loadError}
        </p>
      )}

      {/* Link surface: the widget when a bot username is configured, else a
          clear "not configured" note. */}
      {botUsername ? (
        <div className="grid gap-2">
          <span className="text-xs font-medium tracking-wide text-nx-text-2 uppercase">
            Link a new approver
          </span>
          <TelegramLoginWidget botUsername={botUsername} onAuth={onAuth} />
          {linking && (
            <p className="text-xs text-nx-text-3">Linking approver…</p>
          )}
        </div>
      ) : (
        botUsername !== null && (
          <div className="rounded-md bg-nx-brand-soft px-3 py-2 text-xs text-nx-brand">
            <p className="font-medium">
              The Telegram login widget is not configured yet.
            </p>
            <p className="mt-1">
              Set TELEGRAM_BOT_USERNAME on the admin service and register the
              bot’s domain in @BotFather — see the runbook.
            </p>
          </div>
        )
      )}

      {/* Linked approvers. */}
      <div className="grid gap-2">
        <span className="text-xs font-medium tracking-wide text-nx-text-2 uppercase">
          Linked approvers ({approvers.length})
        </span>
        {approvers.length === 0 ? (
          <p className="text-sm text-nx-text-3">
            No approvers linked yet — money confirmations from the ops bot stay
            dormant until at least one is linked.
          </p>
        ) : (
          <div className="grid gap-2">
            {approvers.map((a) => (
              <ApproverRow key={a.id} approver={a} onUnlink={setConfirmUnlink} />
            ))}
          </div>
        )}
      </div>

      {/* Unlink confirm — destructive, confirm-gated like the other admin
          writes. */}
      <Modal
        open={confirmUnlink !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmUnlink(null);
        }}
        title="Unlink this approver?"
        description={
          <>
            {confirmUnlink?.username
              ? `@${confirmUnlink.username}`
              : (confirmUnlink?.first_name ?? "This account")}{" "}
            can no longer confirm money (recharge) actions from the ops bot. If
            they are the only approver, the confirmation gate goes dormant. You
            can link them again later.
          </>
        }
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmUnlink(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={unlinking}
              onClick={onUnlink}
            >
              Confirm
            </Button>
          </>
        }
      />
    </div>
  );
}
